import * as THREE from "three";
import { DEFAULT_ENV } from "@rietz/shared";
import {
  FILL_DIR,
  SKY_HORIZON,
  SUN_DIR,
  lightingFor,
  rand01,
  randSym,
  type LightingPreset,
} from "./palette.js";
import {
  SHADOWS_ENABLED,
  SHADOW_BIAS,
  SHADOW_FAR,
  SHADOW_HALF_EXTENT,
  SHADOW_LIGHT_DISTANCE,
  SHADOW_MAP_SIZE,
  SHADOW_NEAR,
  SHADOW_NORMAL_BIAS,
  SHADOW_SNAP_TEXELS,
} from "./shadows.js";

/**
 * Sky dome, distant mountains, and the three lights the whole game is lit by.
 * No shadow maps by default — baked vertex AO (`mapMesh.ts`) fakes the
 * contrast, and the hemisphere is set high for readable interiors; `?shadows=1`
 * turns real shadows on (`shadows.ts`). The dome and mountains are `fog: false`
 * with `depthWrite: false`: a painted backdrop whose horizon band matches the
 * fog colour.
 */

export interface BuiltSky {
  group: THREE.Group;
  triangles: number;
  dispose(): void;
}

export interface SkyOptions {
  /** Dome radius, metres. Must stay inside the camera's far plane. */
  radius: number;
  /** Draw the low-poly mountain ring. */
  mountains: boolean;
  /** World height the mountains stand on. */
  groundY: number;
  /**
   * The hour and weather to paint; defaults to a clear day. The dome's colours
   * are BAKED INTO ITS VERTICES, so a changed environment gets a new sky, not a
   * re-lit one (`WorldView.setEnv`).
   */
  preset?: LightingPreset;
}

export function buildSky(opts: SkyOptions): BuiltSky {
  const preset = opts.preset ?? lightingFor(DEFAULT_ENV);
  const group = new THREE.Group();
  group.name = "sky";

  const disposables: { dispose(): void }[] = [];
  let triangles = 0;

  // --- Gradient dome: height segments matter more than width — the gradient is
  // vertical and steepest just above the horizon, where a sphere's rows are
  // coarsest.
  const domeGeo = new THREE.SphereGeometry(opts.radius, 24, 48);
  domeGeo.deleteAttribute("uv");
  domeGeo.deleteAttribute("normal");
  paintDome(domeGeo, opts.radius, preset);

  const domeMat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    side: THREE.BackSide,
    fog: false,
    depthWrite: false,
    // The shallow horizon gradient bands green/magenta when quantised to 8
    // bits.
    dithering: true,
  });
  const dome = new THREE.Mesh(domeGeo, domeMat);
  dome.name = "skydome";
  dome.frustumCulled = false;
  dome.renderOrder = -1000;
  group.add(dome);
  disposables.push(domeGeo, domeMat);
  triangles += triangleCount(domeGeo);

  // --- Mountain ring -------------------------------------------------------
  if (opts.mountains) {
    const ringGeo = buildMountainRing(opts.radius * 0.66, opts.groundY, preset);
    const ringMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      fog: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Same shallow haze gradient as the dome — see above.
      dithering: true,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.name = "mountains";
    ring.frustumCulled = false;
    ring.renderOrder = -999;
    group.add(ring);
    disposables.push(ringGeo, ringMat);
    triangles += triangleCount(ringGeo);
  }

  return {
    group,
    triangles,
    dispose(): void {
      group.clear();
      for (const d of disposables) d.dispose();
    },
  };
}

export interface BuiltLights {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  fill: THREE.DirectionalLight;
  /**
   * Re-aims the sun's shadow box at a world point — the camera, once per frame.
   * No-op unless `?shadows=1`; allocation-free.
   */
  updateShadowFocus(focus: THREE.Vector3): void;
  dispose(): void;
}

/**
 * The lighting rig, created once and kept across map changes. Under
 * `?shadows=1` the SUN is the only caster; fill and hemisphere stay shadowless
 * so a north wall is not a black hole.
 */
export function createLights(): BuiltLights {
  const group = new THREE.Group();
  group.name = "lights";

  const hemi = new THREE.HemisphereLight();
  group.add(hemi);

  const sun = new THREE.DirectionalLight();
  group.add(sun);
  group.add(sun.target);

  const fill = new THREE.DirectionalLight();
  fill.position.set(FILL_DIR.x * 100, FILL_DIR.y * 100, FILL_DIR.z * 100);
  group.add(fill);
  group.add(fill.target);

  if (SHADOWS_ENABLED) configureSunShadow(sun);

  // Born under a clear day; a raid overwrites this from its `welcome`
  // (`WorldView.setEnv`).
  writeLights(sun, hemi, fill, lightingFor(DEFAULT_ENV));

  return {
    group,
    sun,
    hemi,
    fill,
    updateShadowFocus(focus: THREE.Vector3): void {
      if (!SHADOWS_ENABLED) return;
      snapFocus(focus, snappedFocus);
      sun.target.position.copy(snappedFocus);
      sun.position
        .copy(snappedFocus)
        .addScaledVector(sunAxis, SHADOW_LIGHT_DISTANCE);
    },
    dispose(): void {
      hemi.dispose();
      sun.dispose();
      fill.dispose();
      group.clear();
    },
  };
}

/**
 * Re-lights the scene for one environment: the three lights, the fog, the clear
 * colour — and, via a rebuilt dome (the caller's job), the sky. A handful of
 * times a session; nothing per-frame or per-material, so a night raid costs
 * what a day raid does.
 */
export function applyLighting(
  lights: BuiltLights,
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  preset: LightingPreset,
): void {
  writeLights(lights.sun, lights.hemi, lights.fill, preset);

  // `FogExp2` is what the view installs; the guard keeps a future linear fog
  // from being a silent no-op.
  const fog = scene.fog;
  if (fog instanceof THREE.FogExp2) {
    fog.color.setHex(preset.fogColor);
    fog.density = preset.fogDensity;
  } else if (fog instanceof THREE.Fog) {
    fog.color.setHex(preset.fogColor);
  }

  // The horizon band and not the zenith: the clear colour shows where the dome
  // does not, which is the rim of the view, and the rim of the view is horizon.
  renderer.setClearColor(preset.skyHorizon, 1);
}

/**
 * DOUBLE-DARKENING COMPENSATION: baked AO plus a real shadow would pay twice
 * for the same missing light, so the ambient floor comes up 20 % under shadows.
 * A sunlit surface gains ~8 %, a shadowed one the full ~16 %. RELATIVE, because
 * at night the hemisphere IS the light.
 */
const SHADOW_HEMI_COMPENSATION = 1.2;

/**
 * Pushes one preset into the three lights — the only writer. The fill's
 * direction is always `FILL_DIR`: it exists so faces the sun cannot reach are
 * shaded rather than black.
 */
function writeLights(
  sun: THREE.DirectionalLight,
  hemi: THREE.HemisphereLight,
  fill: THREE.DirectionalLight,
  preset: LightingPreset,
): void {
  hemi.color.setHex(preset.hemiSky);
  hemi.groundColor.setHex(preset.hemiGround);
  hemi.intensity = SHADOWS_ENABLED
    ? preset.hemiIntensity * SHADOW_HEMI_COMPENSATION
    : preset.hemiIntensity;

  sun.color.setHex(preset.sunColor);
  sun.intensity = preset.sunIntensity;
  aimSun(sun, preset);

  fill.color.setHex(preset.fillColor);
  fill.intensity = preset.fillIntensity;
}

/**
 * Points the sun (or moon) where the preset says and re-derives the shadow
 * map's basis — `snapFocus` snaps in the light's own axes, and a stale basis is
 * a shimmering shadow map.
 */
function aimSun(sun: THREE.DirectionalLight, preset: LightingPreset): void {
  sunAxis.set(preset.sunDir.x, preset.sunDir.y, preset.sunDir.z).normalize();
  shadowRight.crossVectors(WORLD_UP, sunAxis).normalize();
  shadowUp.crossVectors(sunAxis, shadowRight);
  // Directional lights only care about direction; with shadows on,
  // `updateShadowFocus` overwrites the DISTANCE every frame.
  sun.position
    .copy(sunAxis)
    .multiplyScalar(SHADOWS_ENABLED ? SHADOW_LIGHT_DISTANCE : 100);
}

// --- Shadow camera (only ever touched under `?shadows=1`) -------------------

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/** Unit vector from the ground TOWARDS the sun. Rewritten by `aimSun`. */
const sunAxis = new THREE.Vector3(SUN_DIR.x, SUN_DIR.y, SUN_DIR.z).normalize();

/**
 * The shadow map's screen axes — what `Matrix4.lookAt` derives for the shadow
 * camera, constant while the sun is, so texel snapping is two dot products.
 * `aimSun` is the sole writer.
 */
const shadowRight = new THREE.Vector3()
  .crossVectors(WORLD_UP, sunAxis)
  .normalize();
const shadowUp = new THREE.Vector3().crossVectors(sunAxis, shadowRight);

/** World size of one shadow texel, and the grid the focus is snapped to. */
const TEXEL_M = (SHADOW_HALF_EXTENT * 2) / SHADOW_MAP_SIZE;
/** How far a horizon channel may be brighter than the day's. See the grade. */
const MOUNTAIN_GRADE_MAX = 1.25;
const SNAP_M = TEXEL_M * SHADOW_SNAP_TEXELS;

/** Scratch. `updateShadowFocus` runs every frame and MUST NOT allocate. */
const snappedFocus = new THREE.Vector3();

function configureSunShadow(sun: THREE.DirectionalLight): void {
  sun.castShadow = true;

  const cam = sun.shadow.camera;
  cam.left = -SHADOW_HALF_EXTENT;
  cam.right = SHADOW_HALF_EXTENT;
  cam.top = SHADOW_HALF_EXTENT;
  cam.bottom = -SHADOW_HALF_EXTENT;
  cam.near = SHADOW_NEAR;
  cam.far = SHADOW_FAR;
  // The extents never change again — only the camera's POSITION does, and
  // three recomputes that from the light every frame.
  cam.updateProjectionMatrix();

  sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  sun.shadow.bias = SHADOW_BIAS;
  sun.shadow.normalBias = SHADOW_NORMAL_BIAS;

  // Park the light at its working distance immediately, so the very first
  // frame — drawn before any `updateShadowFocus` — is already framed.
  sun.position.copy(sunAxis).multiplyScalar(SHADOW_LIGHT_DISTANCE);
}

/**
 * Rounds `focus` onto the shadow map's texel grid, in the LIGHT's basis
 * (snapping world axes lands on whole metres but fractional texels). All three
 * components, so between snaps the shadow camera does not move at all.
 */
function snapFocus(focus: THREE.Vector3, out: THREE.Vector3): void {
  const r = Math.round(focus.dot(shadowRight) / SNAP_M) * SNAP_M;
  const u = Math.round(focus.dot(shadowUp) / SNAP_M) * SNAP_M;
  const d = Math.round(focus.dot(sunAxis) / SNAP_M) * SNAP_M;
  out.set(0, 0, 0);
  out.addScaledVector(shadowRight, r);
  out.addScaledVector(shadowUp, u);
  out.addScaledVector(sunAxis, d);
}

// ---------------------------------------------------------------------------

function paintDome(
  geo: THREE.BufferGeometry,
  radius: number,
  preset: LightingPreset,
): void {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const zenith = new THREE.Color(preset.skyZenith);
  const horizon = new THREE.Color(preset.skyHorizon);
  const colors = new Float32Array(pos.count * 3);

  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i) / radius;
    // Haze hugs the horizon: raise the ratio to a power so the blue only takes
    // over well above the skyline, the way it actually looks in a valley.
    const t = Math.pow(Math.max(0, y), 0.55);
    const o = i * 3;
    colors[o] = horizon.r + (zenith.r - horizon.r) * t;
    colors[o + 1] = horizon.g + (zenith.g - horizon.g) * t;
    colors[o + 2] = horizon.b + (zenith.b - horizon.b) * t;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

/**
 * Two staggered rings of 4-sided cones: a hazy far range and a sharper near
 * one. Never collided with, never animated, outside the playable bounds.
 */
function buildMountainRing(
  radius: number,
  groundY: number,
  preset: LightingPreset,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const colors: number[] = [];

  // Heights are fractions of the ring's distance: what sells a mountain is the
  // angle it subtends, ~20° here.
  const RINGS = [
    // [distance scale, count, height fraction range, colour, snow bias]
    { rs: 1.0, count: 26, hMin: 0.18, hMax: 0.3, hex: 0x6e86a4, snow: 0.62 },
    { rs: 1.24, count: 20, hMin: 0.26, hMax: 0.45, hex: 0x8fa3ba, snow: 0.55 },
  ] as const;

  const snowColor = new THREE.Color(0xe9eef4);
  const haze = new THREE.Color(preset.skyHorizon);
  const rock = new THREE.Color();

  // The ridge is lit by the same sky as the valley: a per-channel RATIO between
  // this horizon and the clear-day one the rock and snow colours were picked
  // against — 1 by day, a fifth at night, alpenglow at dawn. Clamped a little
  // above 1 so a sunrise snowfield does not blow out.
  const day = new THREE.Color(SKY_HORIZON);
  const lit = new THREE.Color(preset.skyHorizon);
  const grade = new THREE.Color(
    Math.min(lit.r / day.r, MOUNTAIN_GRADE_MAX),
    Math.min(lit.g / day.g, MOUNTAIN_GRADE_MAX),
    Math.min(lit.b / day.b, MOUNTAIN_GRADE_MAX),
  );

  for (let r = 0; r < RINGS.length; r++) {
    const ring = RINGS[r];
    if (ring === undefined) continue;
    rock.setHex(ring.hex);

    for (let i = 0; i < ring.count; i++) {
      const seed = r * 977 + i;
      const angle =
        ((i + 0.5) / ring.count) * Math.PI * 2 + randSym(seed, 1) * 0.06;
      const dist = radius * ring.rs * (1 + randSym(seed, 2) * 0.06);
      const cx = Math.cos(angle) * dist;
      const cz = Math.sin(angle) * dist;
      const height =
        dist * (ring.hMin + rand01(seed, 3) * (ring.hMax - ring.hMin));
      const baseR = height * (0.5 + rand01(seed, 4) * 0.35);
      const tilt = randSym(seed, 5) * 0.12;

      // A 4-sided pyramid, rotated so the ridges do not all line up.
      const apexX = cx + Math.cos(angle) * tilt * height * 0.2;
      const apexY = groundY + height;
      const apexZ = cz + Math.sin(angle) * tilt * height * 0.2;
      const spin = rand01(seed, 6) * Math.PI * 2;

      for (let s = 0; s < 4; s++) {
        const a0 = spin + (s / 4) * Math.PI * 2;
        const a1 = spin + ((s + 1) / 4) * Math.PI * 2;
        const r0 = baseR * (0.75 + rand01(seed, 10 + s) * 0.5);
        const r1 = baseR * (0.75 + rand01(seed, 10 + ((s + 1) % 4)) * 0.5);

        const x0 = cx + Math.cos(a0) * r0;
        const z0 = cz + Math.sin(a0) * r0;
        const x1 = cx + Math.cos(a1) * r1;
        const z1 = cz + Math.sin(a1) * r1;

        positions.push(x0, groundY, z0, x1, groundY, z1, apexX, apexY, apexZ);

        // Base vertices sit in the haze; the apex is rock or snow.
        pushColor(colors, haze);
        pushColor(colors, haze);
        rock.setHex(ring.hex);
        // Only the taller half of each ring gets a snow cap, which is what
        // gives the skyline its variety at zero cost.
        if (rand01(seed, 20) < 0.55 && height > dist * ring.snow * 0.42) {
          rock.lerp(snowColor, 0.6);
        }
        rock.multiply(grade);
        pushColor(colors, rock);
      }
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(positions), 3),
  );
  geo.setAttribute(
    "color",
    new THREE.BufferAttribute(new Float32Array(colors), 3),
  );
  geo.computeBoundingSphere();
  return geo;
}

function pushColor(out: number[], c: THREE.Color): void {
  out.push(c.r, c.g, c.b);
}

function triangleCount(geo: THREE.BufferGeometry): number {
  const index = geo.getIndex();
  const verts = index !== null ? index.count : geo.attributes.position?.count ?? 0;
  return verts / 3;
}
